import { useContext, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useQuery } from "@apollo/client"
import _ from "lodash"
import { DateTime } from "luxon"
import GLOBALS_STATUS from "../../config/globalsStatus"
import { UserContext } from "../../context/userContext"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { Button } from "primereact/button"

// Comps
import LoaderFullPage from "../../components/Loaders"
import ParticipantSidebar from "../../components/sidebars/ParticipantSidebar"
import ParticipantProfileBoxSmall from "../../components/ParticipantProfileBoxSmall"

// Helpers
import { navigateWithOrigin } from "../../helpers/navigation"
import {
  ClassifyEpisode,
  GetEpisodeDate,
} from "./helpers/serviceEpisodeHelpers"
import { isOrganizationAdmin } from "../../helpers/staffRoleHelpers"

import {
  GET_MAX_SERVICE_EPISODE,
  GET_PAST_SERVICE_EPISODE_SUMMARIZED,
} from "./queries"

import styles from "../../styles/Page-Engagements.module.css"

export default function ServicesEpisodes() {
  const [maxServiceEpisodes, setMaxServiceEpisodes] = useState(null)
  const [pastServiceEpisodes, setPastServiceEpisodes] = useState(null)
  const userCtx = useContext(UserContext)
  const navigate = useNavigate()

  const convertDate = (date) => {
    const parsedDate = DateTime.fromISO(date)
    return parsedDate.toFormat("MM/dd/yyyy")
  }

  const ActionButton = ({ label, navTo }) => {
    return (
      <Button
        label={label}
        onClick={() => {
          navigateWithOrigin(navigate, navTo)
        }}
      />
    )
  }

  const { loading: loadingMaxServiceEpisode } = useQuery(
    GET_MAX_SERVICE_EPISODE,
    {
      variables: {
        participant_id: userCtx.participant_id,
        organization_id: userCtx.session.user.organization_id,
        staff_id: userCtx.session.user.staff_id,
        participant_membership_id: userCtx.participant_membership_id,
      },
      fetchPolicy: "network-only",
      skip: !userCtx.participant_id,
      onCompleted: async (data) => {
        if (data.getMaxServiceEpisode === null) {
          return setMaxServiceEpisodes(null)
        }

        const episodesList = data?.getMaxServiceEpisode || []

        const handleData = _.map(episodesList, (episode) => {
          const viewIntake = episode.episode_code_id === "100"
          const viewDischarge = episode.episode_code_id === "200"
          const editIntake =
            episode.episode_code_id === "100" &&
            isOrganizationAdmin(userCtx.session.user.staff_role, true)
          const viewServiceRecord = _.includes(
            ["101", "102", "103", "104", "105", "106"],
            episode.episode_code_id,
          )

          let actionButton

          if (viewIntake) {
            const activeUid =
              episode?.episode_intake_uid || episode?.manage_services_uid
            const templateName = episode?.template_name

            if (activeUid) {
              actionButton = (
                <div className="flex flex-row gap-2">
                  <ActionButton
                    label="View"
                    navTo={`/form/view/${templateName}/${activeUid}`}
                  />
                  {editIntake && (
                    <ActionButton
                      label="Edit"
                      navTo={`/form/${templateName}/1/${activeUid}`}
                    />
                  )}
                </div>
              )
            }
          }

          if (viewServiceRecord && episode?.manage_services_uid) {
            actionButton = (
              <ActionButton
                label="View"
                navTo={`/form/view/manage_services/${episode.manage_services_uid}`}
              />
            )
          }

          if (viewDischarge && episode?.episode_discharge_uid) {
            actionButton = (
              <ActionButton
                label="View"
                navTo={`/form/view/discharge_session/${episode.episode_discharge_uid}`}
              />
            )
          }

          const date = GetEpisodeDate(episode)
          const { episode_name, detailEpisode } = ClassifyEpisode(episode)

          return {
            service_episodes_id: episode?.service_episodes_id,
            episode_intake_date: episode?.episode_intake_date,
            manage_services_date: episode?.manage_services_date,
            date: convertDate(date),
            episode_code_id: episode_name,
            program_ids: episode?.program_ids,
            staff_ids: episode?.staff_ids,
            location_ids: episode?.location_ids,
            edit: actionButton,
            detailEpisode: detailEpisode,
          }
        })
        setMaxServiceEpisodes(handleData)
      },
    },
  )

  const { loading: loadingPastServicesEpisode } = useQuery(
    GET_PAST_SERVICE_EPISODE_SUMMARIZED,
    {
      variables: {
        participant_id: userCtx.participant_id,
        organization_id: userCtx.session.user.organization_id,
        staff_id: userCtx.session.user.staff_id,
        participant_membership_id: userCtx.participant_membership_id,
      },
      fetchPolicy: "network-only",
      skip: !userCtx.participant_id,
      onCompleted: async (data) => {
        if (data.getPastServiceEpisodesSummarized === null) {
          setPastServiceEpisodes(null)
        }
        const episodesList = data?.getPastServiceEpisodesSummarized || []

        const handleData = _.map(episodesList, (episode) => {
          const actionButton = (
            <Button
              label="View"
              to={`/participant/manage/service-episode/${episode?.episode_number}`}
              onClick={() => {
                navigate(
                  `/participant/manage/service-episode/${episode?.episode_number}`,
                )
              }}
            />
          )

          return {
            service_episodes_id: episode?.service_episodes_id,
            episode_intake_date: convertDate(
              episode?.episode_intake_date || episode?.manage_services_date,
            ),
            episode_discharge_date: convertDate(
              episode?.episode_discharge_date,
            ),
            edit: actionButton,
          }
        })
        setPastServiceEpisodes(handleData)
      },
    },
  )

  const loading = loadingMaxServiceEpisode || loadingPastServicesEpisode

  if (loading) return <LoaderFullPage />

  const handleNewEpisodeBtn =
    _.includes(
      GLOBALS_STATUS.ACTIVE_STATUS,
      userCtx.participant_membership_status,
    ) || userCtx.participant_membership_status === "Kiosk Member" ? (
      <Link to="/form/manage_services/1/" className="btn mb-5">
        Manage Service Episode
      </Link>
    ) : null

  const handleEditEpisodeBtn =
    !_.includes(
      GLOBALS_STATUS.ACTIVE_STATUS,
      userCtx.participant_membership_status,
    ) && userCtx.participant_membership_status !== "Kiosk Member" ? (
      <Link to="/form/intake_reactivation_session/1/" className="btn mb-5">
        Start New Service Episode
      </Link>
    ) : null

  return (
    <div className="layout-with-sidebar">
      <div className="sidebar">
        <ParticipantSidebar />
      </div>
      <div className="main-content">
        <ParticipantProfileBoxSmall />
        <div>
          <div>
            <h1 className={styles.title}>Service Episodes</h1>
            <div className="mb-5">
              <p>
                Service records are generated on a time delay and should be
                available 15 minutes after completion.
              </p>
            </div>

            {handleNewEpisodeBtn}
            {handleEditEpisodeBtn}

            <h2 className={styles.subTitle}>Current Service Episode</h2>

            <DataTable
              value={maxServiceEpisodes}
              responsiveLayout="scroll"
              className="mb-5"
            >
              <Column field="date" header="Service Date" />
              <Column field="episode_code_id" header="Service Type" />
              {/*<Column field="program_ids" header="Program ID Assign" />
              <Column field="staff_ids" header="Staff Assign" />
              <Column field="location_ids" header="Location Assign" />*/}
              <Column field="detailEpisode" header="Service Detail" />
              <Column field="edit" header="Actions" />
            </DataTable>

            <h2 className={styles.subTitle}>Past Service Episodes</h2>
            <DataTable
              value={pastServiceEpisodes}
              responsiveLayout="scroll"
              className="mb-5"
            >
              <Column field="episode_intake_date" header="Intake Date" />
              <Column field="episode_discharge_date" header="Discharge Date" />
              <Column field="edit" header="Actions" />
            </DataTable>
          </div>
        </div>
      </div>
    </div>
  )
}
